import { createContext, useContext, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import subscriptionService, { SubscriptionStatusPayments } from '../services/subscription';
import { useAuth } from './AuthContext';

export interface SubscriptionContextType {
  // --- New fields from payments service (v28-3-1) ---
  tier: 'free' | 'pro' | 'premium';
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | null;
  trialExpiresAt: string | null;
  trialExhausted: boolean;
  plan: 'pro' | 'premium' | null;
  isTrialing: boolean;
  isActive: boolean;
  isPastDue: boolean;
  isCancelled: boolean;
  isLoading: boolean;
  isPlaceholderData: boolean;  // true while placeholder (first fetch in-flight) — use for gating UI

  // --- Legacy helpers preserved for existing callers ---
  isPro: boolean;      // true for pro OR premium when active/trialing/past_due
  isPremium: boolean;  // true for premium only when active/trialing/past_due
  canAccessExchange: (exchange: string) => boolean;
  checkLimit: (resourceType: string, currentCount: number) => boolean;
  getLimit: (resourceType: string) => number;
  refreshSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Exchanges accessible to Free tier users
const FREE_EXCHANGES = new Set(['JSE', 'FOREX', 'CC', 'INDX']);
// Exchanges that require Pro or Premium
const PRO_ONLY_EXCHANGES = new Set(['US', 'NASDAQ', 'NYSE', 'LSE', 'XETRA', 'PA', 'AS', 'MI']);

const FREE_LIMITS = { watchlists: 1, portfolios: 1, alerts: 5, ai_requests: 5 };
const PRO_LIMITS = { watchlists: 10, portfolios: 10, alerts: 100, ai_requests: 50 };

export const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, isPlaceholderData } = useQuery<SubscriptionStatusPayments>({
    queryKey: ['subscription', 'status'],
    queryFn: () => subscriptionService.getStatus(),
    staleTime: 60_000,           // 60 s — within backend 120 s Redis TTL
    refetchInterval: 60_000,     // poll every 60 s
    refetchOnWindowFocus: true,  // override global false — re-fetch after payment callback tab-switch (AC6)
    enabled: !!isAuthenticated,
    // Return safe defaults on error so the app never crashes due to subscription fetch failure
    placeholderData: {
      tier: 'free',
      status: null,
      trial_expires_at: null,
      trial_exhausted: false,
      plan: null,
    },
  });

  const tier = data?.tier ?? 'free';
  const status = data?.status ?? null;
  const trialExpiresAt = data?.trial_expires_at ?? null;
  const trialExhausted = data?.trial_exhausted ?? false;
  const plan = data?.plan ?? null;

  const isTrialing = status === 'trialing';
  const isActive = status === 'active';
  const isPastDue = status === 'past_due';
  const isCancelled = status === 'cancelled';

  // Legacy helpers — maintained for backward compat with existing components
  const hasPaidAccess = isTrialing || isActive || isPastDue;
  const isPro = (tier === 'pro' || tier === 'premium') && hasPaidAccess;
  const isPremium = tier === 'premium' && hasPaidAccess;

  const canAccessExchange = (exchange: string): boolean => {
    let exchangeCode = exchange;
    if (exchange.includes('.')) {
      exchangeCode = exchange.split('.').pop()?.toUpperCase() || '';
    }
    if (FREE_EXCHANGES.has(exchangeCode)) return true;
    if (PRO_ONLY_EXCHANGES.has(exchangeCode)) return isPro;
    return true;
  };

  const getLimit = (resourceType: string): number => {
    const limits = isPro ? PRO_LIMITS : FREE_LIMITS;
    return limits[resourceType as keyof typeof limits] || 0;
  };

  const checkLimit = (resourceType: string, currentCount: number): boolean =>
    currentCount < getLimit(resourceType);

  const refreshSubscription = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', 'status'] });
  };

  const value: SubscriptionContextType = {
    tier,
    status,
    trialExpiresAt,
    trialExhausted,
    plan,
    isTrialing,
    isActive,
    isPastDue,
    isCancelled,
    isLoading,
    isPlaceholderData,
    isPro,
    isPremium,
    canAccessExchange,
    checkLimit,
    getLimit,
    refreshSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

// HOC for Pro-only features — preserved for backward compat
export function withProAccess<T extends object>(
  WrappedComponent: React.ComponentType<T>,
  fallbackMessage?: string
) {
  return function ProAccessWrapper(props: T) {
    const { isPro, isLoading, isPlaceholderData } = useSubscription();

    if (isLoading || isPlaceholderData) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full" />
        </div>
      );
    }

    if (!isPro) {
      return (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Pro Feature
          </h3>
          <p className="text-yellow-700 dark:text-yellow-300 mb-4">
            {fallbackMessage || 'This feature requires a Pro subscription.'}
          </p>
          <a
            href="/upgrade"
            className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Upgrade to Pro
          </a>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
}
